import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Default colors for options
const OPTION_COLORS = [
  '#22c55e', // Green (positive)
  '#ef4444', // Red (negative)
  '#f59e0b', // Amber
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
];

// Auto-assign colors based on positive/negative keywords
const POSITIVE_KEYWORDS = ['yes', 'y', 'affirmative', 'positive', 'true', 'accept', 'approve', 'confirm', 'ok', 'okay', 'agree', 'correct', 'right'];
const NEGATIVE_KEYWORDS = ['no', 'n', 'negative', 'false', 'reject', 'deny', 'decline', 'cancel', 'disagree', 'wrong', 'incorrect'];
const POSITIVE_COLOR = '#22c55e';
const NEGATIVE_COLOR = '#ef4444';

// Get auto-color based on label
const getAutoColor = (label, fallbackColor) => {
  const labelLower = label?.toLowerCase?.() || '';
  if (POSITIVE_KEYWORDS.includes(labelLower)) return POSITIVE_COLOR;
  if (NEGATIVE_KEYWORDS.includes(labelLower)) return NEGATIVE_COLOR;
  return fallbackColor;
};

// Default options for new decision nodes
export const DEFAULT_OPTIONS = [
  { id: 'yes', label: 'Yes', color: POSITIVE_COLOR },
  { id: 'no', label: 'No', color: NEGATIVE_COLOR },
];

/**
 * DecisionOptionsModal
 * 
 * Modal for configuring decision node options (2-5 options).
 */
const DecisionOptionsModal = ({
  isOpen,
  options = DEFAULT_OPTIONS,
  onSave,
  onClose,
}) => {
  const [localOptions, setLocalOptions] = useState(options);

  // Sync with prop changes
  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  if (!isOpen) return null;

  const handleLabelChange = (id, newLabel) => {
    setLocalOptions(prev => 
      prev.map(opt => {
        if (opt.id !== id) return opt;
        // Auto-assign color based on positive/negative keywords
        const autoColor = getAutoColor(newLabel, opt.color);
        return { ...opt, label: newLabel, color: autoColor };
      })
    );
  };

  const handleColorChange = (id, newColor) => {
    setLocalOptions(prev => 
      prev.map(opt => opt.id === id ? { ...opt, color: newColor } : opt)
    );
  };

  const handleAddOption = () => {
    if (localOptions.length >= 5) return;
    
    const newId = `opt-${Date.now()}`;
    const colorIndex = localOptions.length % OPTION_COLORS.length;
    setLocalOptions(prev => [
      ...prev,
      { id: newId, label: `Option ${prev.length + 1}`, color: OPTION_COLORS[colorIndex] }
    ]);
  };

  const handleRemoveOption = (id) => {
    if (localOptions.length <= 2) return;
    setLocalOptions(prev => prev.filter(opt => opt.id !== id));
  };

  const handleSave = () => {
    // Validate - ensure all labels are non-empty
    const validOptions = localOptions.map(opt => ({
      ...opt,
      label: opt.label.trim() || 'Option'
    }));
    onSave(validOptions);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="decision-options-modal__backdrop" onClick={handleBackdropClick}>
      <div className="decision-options-modal">
        <div className="decision-options-modal__header">
          <h3 className="decision-options-modal__title">Decision Options</h3>
          <button 
            className="decision-options-modal__close"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="decision-options-modal__body">
          <p className="decision-options-modal__hint">
            Configure 2-5 options for this decision. Each option creates a separate branch.
          </p>

          <div className="decision-options-modal__list">
            {localOptions.map((option, index) => (
              <div key={option.id} className="decision-options-modal__option">
                <span className="decision-options-modal__option-number">{index + 1}</span>
                
                <input
                  type="text"
                  className="decision-options-modal__option-label"
                  value={option.label}
                  onChange={(e) => handleLabelChange(option.id, e.target.value)}
                  placeholder="Option label"
                  maxLength={20}
                />

                <div className="decision-options-modal__option-colors">
                  {OPTION_COLORS.map(color => (
                    <button
                      key={color}
                      className={`decision-options-modal__color-btn ${option.color === color ? 'decision-options-modal__color-btn--active' : ''}`}
                      style={{ background: color }}
                      onClick={() => handleColorChange(option.id, color)}
                      title={color}
                    />
                  ))}
                </div>

                <button
                  className="decision-options-modal__remove-btn"
                  onClick={() => handleRemoveOption(option.id)}
                  disabled={localOptions.length <= 2}
                  title={localOptions.length <= 2 ? "Minimum 2 options required" : "Remove option"}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {localOptions.length < 5 && (
            <button 
              className="decision-options-modal__add-btn"
              onClick={handleAddOption}
            >
              + Add Option
            </button>
          )}
        </div>

        <div className="decision-options-modal__footer">
          <button 
            className="decision-options-modal__cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="decision-options-modal__save-btn"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default DecisionOptionsModal;

